/**
 * Maps API error codes to short, human strings for the owner + staff UI.
 * Inline field validation is handled by RHF/Zod — these cover server errors.
 * Unknown codes fall back to a generic message.
 */
const ERROR_MESSAGES: Record<string, string> = {
  // Generic
  VALIDATION_ERROR: 'Some fields are invalid — check the form and try again.',
  // Covers both wrong-credentials at login AND mid-session expiry. Login
  // failures land this message on the login form (where "session ended" was
  // nonsense); a mid-session 401 is followed by a redirect to login, so a
  // generic copy works for both.
  UNAUTHORIZED: 'Login failed — check your credentials and try again.',
  FORBIDDEN: "You don't have permission to do that.",
  NOT_FOUND: "That couldn't be found.",
  CONFLICT: 'That conflicts with an existing record.',
  NETWORK_ERROR: 'Network request failed — check your connection and retry.',

  // Tenant lifecycle
  TENANT_SUSPENDED:
    'This account is currently suspended. Please contact support to reactivate.',
  TENANT_PENDING_SETUP: 'This account is still in setup.',

  // Logo upload
  LOGO_INVALID_FORMAT: 'Logo must be a PNG or JPEG image.',
  LOGO_TOO_LARGE: 'Logo file is too large — the maximum is 5MB.',
  LOGO_NOT_SQUARE: 'Logo must be square — its width and height must match.',
  LOGO_TOO_SMALL: 'Logo must be at least 300×300 pixels.',
  LOGO_TOO_LARGE_DIMENSIONS: 'Logo must be at most 1500×1500 pixels.',

  // Pass builder (slice 4a-3)
  PROGRAM_ALREADY_EXISTS: 'This business already has a loyalty program.',
  PROGRAM_NOT_FOUND: 'No loyalty program exists yet.',
  // SLICE-PASS-TYPES — admin-gated program caps.
  PROGRAM_CAP_REACHED:
    "You've reached the number of programs included in your plan. Contact Naya to add more.",
  PASS_TYPE_NOT_ALLOWED:
    'That pass type is not enabled for your business yet. Contact Naya to turn it on.',
  PROGRAM_AMBIGUOUS:
    'This business has more than one active program — pick which one to use.',
  STRUCTURAL_LOCKED:
    "The stamps, milestones, and completion behavior are locked once the program is active and can't be changed.",
  PROGRAM_ACTIVATION_INCOMPLETE:
    "The program is missing some required info — check the review step before activating.",
  STAMP_ICON_INVALID_FORMAT: 'Stamp icon must be a PNG image.',
  STAMP_ICON_TOO_LARGE: 'Stamp icon file is too large — the maximum is 512KB.',
  STAMP_ICON_NOT_SQUARE: 'Stamp icon must be square.',
  STAMP_ICON_WRONG_DIMENSIONS: 'Stamp icon must be 200×200 pixels.',

  // Redemption (slice 4a-5)
  MILESTONE_NOT_FOUND: "That reward isn't part of this card's program.",
  MILESTONE_NOT_EARNED:
    'Not enough stamps yet — the customer hasn’t earned this reward.',
  MILESTONE_ALREADY_REDEEMED: 'That reward has already been redeemed.',
  CARD_NOT_ACTIVE: 'This card is no longer active.',
  CARD_NOT_RESETTABLE:
    'Only a completed card on a “mark complete” program can be reset.',
  STAFF_BRANCH_MISMATCH: 'You can only redeem at your assigned branch.',
  STAFF_RECOVER_PASS_DISABLED:
    'Staff pass recovery is off for this program. Ask the owner to recover this pass.',

  // Stamping (slice 4b)
  CARD_FULL:
    'Card is already full — claim a reward before issuing more stamps.',
  BRANCH_ARCHIVED:
    'This branch is archived — stamps can only be issued from active branches.',
  STAMP_TOKEN_INVALID:
    "This QR isn't a valid Naya pass. Try re-scanning, or ask the customer to refresh their wallet.",
  STAMP_TOKEN_EXPIRED:
    'This QR is too old. Ask the customer to refresh their wallet pass.',

  // Membership (SLICE-MEMBERSHIP-PASS)
  MEMBERSHIP_CARD:
    'This is a membership card — verify it instead of stamping.',
  NOT_MEMBERSHIP_CARD: 'This card is not a membership.',
  MEMBERSHIP_NOT_RENEWABLE:
    'This membership has no renewable term — only relative-term memberships can be renewed.',

  // Event ticket (SLICE-EVENT-TICKET-PASS)
  TICKET_ALREADY_USED: 'This ticket has already been used.',
  TICKET_VOID: 'This ticket has been cancelled.',
  TICKET_NOT_PAID: 'This ticket has not been paid for yet.',
  NOT_EVENT_TICKET: 'This card is not an event ticket.',

  // Customer lifecycle (2026-09-05) — fallback for the rare race where a
  // customer is revoked/erased between card lookup and slide-to-stamp. The
  // normal path is caught earlier (see StampOverlayBlocked) and never hits
  // this code.
  CUSTOMER_REVOKED: 'This customer has been revoked and cannot earn new stamps.',
  CUSTOMER_DATA_ERASED:
    "This customer's data has been erased and cannot earn new stamps.",
}

const FALLBACK = 'Something went wrong — please try again.'

export function errorMessageFor(code: string): string {
  return ERROR_MESSAGES[code] ?? FALLBACK
}
